import {
  GoalGroupBy,
  GoalSortingMode,
  GroupByMode,
  OrderDirection,
} from '@griply/types';
import { useSortedGoals } from 'features/goals';
import {
  useGoalsOverviewFilterDocument,
  useUpdateGoalSortMode,
  useUpdateUserSettings,
} from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { PageEvents } from 'shared/services/tracking';
import { getOrderedGoalIds } from 'shared/utils/get-ordered-goal-ids';
import { GoalFilterList, SortBar } from 'shared/views/goal-filter-list';

import { useGoalTreeReorder } from '../../hooks/use-goal-tree-reorder';
import { useGoalsOverviewOnSectionCollapse } from '../../hooks/use-goals-overview-on-section-collapse';
import { EmptyBanner } from '../empty-banner';
import { TabPanel } from '../tab-panel';
import * as Styled from './group-by-list.style';

export const GroupByList: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  useTrackPageVisitEvent(PageEvents.GoalsList, undefined);
  const user = useUser();
  const sortedGoals = useSortedGoals();
  const showCompletedGoals =
    user?.settings?.goalsOverview?.roadmap?.showCompletedGoals ?? true;

  const filteredGoals = React.useMemo(() => {
    if (showCompletedGoals) {
      return sortedGoals;
    }
    return showCompletedGoals
      ? sortedGoals
      : sortedGoals.filter((g) => !g.completedAt);
  }, [sortedGoals, showCompletedGoals]);

  const filterDocument = useGoalsOverviewFilterDocument();
  const updateCollapsedSections = useGoalsOverviewOnSectionCollapse();

  const groupBy =
    (user?.settings?.goalGroupBy as GoalGroupBy) ?? GroupByMode.LifeArea;

  const onReorder = useGoalTreeReorder(groupBy);
  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;
  const sortingMode = user?.settings?.goalSortingMode;
  const customOrder = useMemo(
    () => getOrderedGoalIds(sortedGoals, sortingMode, sortDirection),
    [sortedGoals, sortingMode, sortDirection],
  );

  const setSortMode = useUpdateGoalSortMode();
  const updateUserSettings = useUpdateUserSettings();

  const onSortChange = useCallback(
    (mode: GoalSortingMode) => {
      setSortMode(mode);
      updateUserSettings({ goalSortDirection: OrderDirection.Asc });
    },
    [setSortMode, updateUserSettings],
  );

  const onSortDirectionToggle = useCallback(() => {
    updateUserSettings({
      goalSortDirection:
        sortDirection === OrderDirection.Asc
          ? OrderDirection.Desc
          : OrderDirection.Asc,
    });
  }, [sortDirection, updateUserSettings]);

  if (!filteredGoals.length) {
    return <EmptyBanner />;
  }

  return (
    <TabPanel role="tab-panel" ref={scrollRef}>
      <Styled.GroupingContainer>
        <SortBar
          value={sortingMode}
          direction={sortDirection}
          onChange={onSortChange}
          onDirectionToggle={onSortDirectionToggle}
          hideLifeArea={groupBy === GroupByMode.LifeArea}
        />
        <GoalFilterList
          filterDocument={filterDocument}
          onSectionCollapse={updateCollapsedSections}
          customOrder={customOrder}
          onReorder={onReorder}
          hideArchived={
            groupBy !== GroupByMode.None && groupBy !== GroupByMode.LifeArea
          }
          scrollElement={scrollRef}
          hideCompleted={
            !user?.settings?.goalsOverview?.roadmap?.showCompletedGoals
          }
        />
      </Styled.GroupingContainer>
    </TabPanel>
  );
};
